import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

export type HealthStatus = 'healthy' | 'needs_attention' | 'at_risk';

/**
 * Profile data structure expected by the member detail screen
 */
export interface MemberProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  profile_image_url: string | null;
  phone: string | null;
  email: string | null;
  preferred_contact_method: string | null;
  date_of_birth: string | null;
  gender: string | null;
  marital_status: string | null;
  bio: string | null;
  membership_status: string | null;
  membership_status_id: string | null;
  first_visit_date: string | null;
  how_found_church: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
  grade_level: string | null;
  school_name: string | null;
  allergies: string | null;
  medical_notes: string | null;
}

export interface MemberAttendance {
  health_status: HealthStatus;
  health_score: number;
  attendance_rate: number;
  attended_events: number;
  total_events: number;
  last_attended_at: string | null;
  consecutive_absences: number;
  leader_notes: string | null;
}

export interface GroupInfo {
  group_name: string;
  joined_at: string;
  joined_via: string | null;
}

export interface MemberDetailData {
  profile: MemberProfile;
  attendance: MemberAttendance | null;
  groupInfo: GroupInfo;
  isViewingSelf: boolean;
  isLeader: boolean;
}

/**
 * Backend API response structure
 */
interface MemberDetailApiResponse {
  success: boolean;
  data: {
    id: string;
    displayName: string;
    firstName: string | null;
    lastName: string | null;
    preferredName: string | null;
    profileImageUrl: string | null;
    joinedAt: string;
    joinedVia?: string | null;
    personal?: {
      dateOfBirth: string | null;
      gender: string | null;
      maritalStatus: string | null;
      bio: string | null;
    };
    contact?: {
      email: string | null;
      phone: string | null;
      preferredContactMethod: string | null;
    };
    emergency?: {
      name: string | null;
      phone: string | null;
      relationship: string | null;
    };
    church?: {
      membershipStatus: string | null;
      membershipStatusId: string | null;
      firstVisitDate: string | null;
      howFoundChurch: string | null;
    };
    health?: {
      gradeLevel: string | null;
      schoolName: string | null;
      allergies: string | null;
      medicalNotes: string | null;
    };
    engagement?: {
      healthStatus: HealthStatus;
      healthScore: number;
      attendanceRate: number | null;
      attendanceCount: number;
      consecutiveAbsences: number;
      lastAttendedAt: string | null;
      leaderNotes: string | null;
    };
  };
  meta: {
    isLeader: boolean;
  };
}

/**
 * Fetch detailed member profile from roster
 */
export function useMemberDetail(
  userId: string | undefined,
  groupId: string | undefined,
  groupName?: string
) {
  const { user, currentGroup, isSwitchingOrg } = useAuth();
  const activeGroupId = groupId || currentGroup?.id;

  return useQuery({
    queryKey: ['member-detail', userId, activeGroupId],
    queryFn: async (): Promise<MemberDetailData> => {
      const response = await api.get<MemberDetailApiResponse>(
        `/roster/${userId}?groupId=${activeGroupId}`
      );

      const data = response.data;
      const meta = response.meta;

      // Transform backend response to frontend expected structure
      const profile: MemberProfile = {
        id: data.id,
        first_name: data.firstName,
        last_name: data.lastName,
        preferred_name: data.preferredName,
        profile_image_url: data.profileImageUrl,
        phone: data.contact?.phone ?? null,
        email: data.contact?.email ?? null,
        preferred_contact_method: data.contact?.preferredContactMethod ?? null,
        date_of_birth: data.personal?.dateOfBirth ?? null,
        gender: data.personal?.gender ?? null,
        marital_status: data.personal?.maritalStatus ?? null,
        bio: data.personal?.bio ?? null,
        membership_status: data.church?.membershipStatus ?? null,
        membership_status_id: data.church?.membershipStatusId ?? null,
        first_visit_date: data.church?.firstVisitDate ?? null,
        how_found_church: data.church?.howFoundChurch ?? null,
        emergency_contact_name: data.emergency?.name ?? null,
        emergency_contact_phone: data.emergency?.phone ?? null,
        emergency_contact_relationship: data.emergency?.relationship ?? null,
        grade_level: data.health?.gradeLevel ?? null,
        school_name: data.health?.schoolName ?? null,
        allergies: data.health?.allergies ?? null,
        medical_notes: data.health?.medicalNotes ?? null,
      };

      // Transform engagement to attendance structure
      // Note: Backend returns attendance_rate as percentage (0-100), not decimal
      let attendance: MemberAttendance | null = null;
      if (data.engagement) {
        attendance = {
          health_status: data.engagement.healthStatus,
          health_score: data.engagement.healthScore,
          attendance_rate: data.engagement.attendanceRate ?? 0,
          attended_events: data.engagement.attendanceCount,
          total_events: data.engagement.attendanceCount, // Backend doesn't provide separate total
          last_attended_at: data.engagement.lastAttendedAt,
          consecutive_absences: data.engagement.consecutiveAbsences,
          leader_notes: data.engagement.leaderNotes,
        };
      }

      // Group info — caller passes groupName when viewing a group other
      // than the app-level current one (roster rail)
      const groupInfo: GroupInfo = {
        group_name: groupName ?? currentGroup?.name ?? 'Group',
        joined_at: data.joinedAt,
        joined_via: data.joinedVia ?? null,
      };

      return {
        profile,
        attendance,
        groupInfo,
        isViewingSelf: user?.id === userId,
        isLeader: meta?.isLeader ?? false,
      };
    },
    // Gate on !isSwitchingOrg so refetches during the 1.5–7.5s claims-
    // verification window don't fire with NEW_TOKEN + OLD_GROUP_ID and
    // 403 against the new org's RLS context.
    enabled: !!userId && !!activeGroupId && !isSwitchingOrg,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Get display name for a member
 */
export function getMemberDisplayName(profile: MemberProfile): string {
  if (profile.preferred_name) {
    return profile.last_name
      ? `${profile.preferred_name} ${profile.last_name}`
      : profile.preferred_name;
  }
  if (profile.first_name) {
    return profile.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile.first_name;
  }
  return 'Unknown';
}
